/**
 * Status – Dienststatus voor klanten (API + optioneel D1 via ?deep=1)
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Database, Headphones } from 'lucide-react';
import { AnimatedSection } from '../components/AnimatedSection';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { SeoMeta } from '../components/SeoMeta';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api';

interface StatusItem {
  name: string;
  customerLabel: string;
  status: 'operational' | 'degraded' | 'outage' | 'checking';
  message?: string;
  lastCheck?: string;
}

export function Status() {
  const [apiStatus, setApiStatus] = useState<StatusItem>({
    name: 'API',
    customerLabel: 'Online dienst',
    status: 'checking',
    lastCheck: undefined,
  });

  const [dbStatus, setDbStatus] = useState<StatusItem>({
    name: 'Database (D1)',
    customerLabel: 'Gegevensopslag',
    status: 'checking',
    lastCheck: undefined,
  });

  useEffect(() => {
    const check = async () => {
      const base = API_BASE.replace(/\/$/, '');

      try {
        const res = await fetch(`${base}/health`);
        const ok = res.ok;
        setApiStatus({
          name: 'API',
          customerLabel: 'Online dienst',
          status: ok ? 'operational' : 'outage',
          message: ok ? undefined : `HTTP ${res.status}`,
          lastCheck: new Date().toISOString(),
        });
      } catch {
        setApiStatus({
          name: 'API',
          customerLabel: 'Online dienst',
          status: 'outage',
          message: 'Geen verbinding',
          lastCheck: new Date().toISOString(),
        });
      }

      try {
        const resD = await fetch(`${base}/health?deep=1`);
        const data = (await resD.json().catch(() => ({}))) as {
          database?: string;
          status?: string;
          message?: string;
        };

        const dbOk = resD.ok && data.database === 'ok';

        setDbStatus({
          name: 'Database (D1)',
          customerLabel: 'Gegevensopslag',
          status: dbOk ? 'operational' : resD.ok ? 'degraded' : 'outage',
          message: !dbOk ? data.message ?? `HTTP ${resD.status}` : undefined,
          lastCheck: new Date().toISOString(),
        });
      } catch {
        setDbStatus({
          name: 'Database (D1)',
          customerLabel: 'Gegevensopslag',
          status: 'outage',
          message: 'Geen verbinding',
          lastCheck: new Date().toISOString(),
        });
      }
    };

    void check();
    const id = setInterval(() => void check(), 60000);
    return () => clearInterval(id);
  }, []);

  const StatusIcon = ({ item }: { item: StatusItem }) => {
    if (item.status === 'checking') return <Loader2 className="h-5 w-5 animate-spin text-text-muted" />;
    if (item.status === 'operational') return <CheckCircle className="h-5 w-5 text-green-600" />;
    if (item.status === 'degraded') return <Database className="h-5 w-5 text-amber-600" />;
    return <XCircle className="h-5 w-5 text-red-600" />;
  };

  const statusLabel = (item: StatusItem) =>
    ({
      operational: 'Alles werkt naar behoren',
      degraded: 'Beperkt — kern blijft bereikbaar',
      outage: 'Storing — we lossen dit op',
      checking: 'Bezig met controleren…',
    })[item.status];

  const statusHint = (item: StatusItem) => {
    if (item.status === 'operational') {
      return 'U kunt CalqTool normaal gebruiken (inloggen, dossiers, berekeningen).';
    }
    if (item.status === 'degraded') {
      return 'De app kan traag zijn of sommige acties tijdelijk mislukken. Probeer het zo opnieuw; bij aanhoudende problemen zie je hieronder meer hulp.';
    }
    if (item.status === 'checking') {
      return 'Een moment geduld — we meten of alles bereikbaar is.';
    }
    return 'Als u nu niet kunt inloggen of opslaan, probeer het over enkele minuten opnieuw. Spoed of vragen? Neem gerust contact op — we helpen u verder.';
  };

  return (
    <div className="animate-page-enter">
      <SeoMeta
        path="/status"
        title="Dienststatus"
        description="Live dienststatus CalqTool™: is de app en API bereikbaar? Automatische checks en tips bij storingen — met link naar probleemoplossing."
      />
      <AnimatedSection delay={0.05}>
        <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Dienststatus' }]} />
        <h1 className="mb-3 text-2xl font-bold text-text">Dienststatus</h1>
        <p className="mb-2 max-w-2xl text-sm leading-relaxed text-text-muted">
          Hier ziet u of CalqTool voor u bereikbaar is. We controleren automatisch of onze online dienst en
          gegevensopslag reageren. Deze pagina vernieuwt ongeveer elke minuut — ververs uw browser als u net een
          wijziging verwacht.
        </p>
        <p className="mb-8 max-w-2xl text-sm leading-relaxed text-text-muted">
          <strong className="text-text">Loopt u vast?</strong> Bekijk eerst de{' '}
          <Link to="/troubleshoot" className="font-medium text-primary hover:underline">
            pagina Problemen oplossen
          </Link>{' '}
          (veelvoorkomende oorzaken bij tekeninganalyse, bibliotheek en inloggen). Blijft het probleem?{' '}
          <Link to="/contact" className="font-medium text-primary hover:underline">
            Neem contact op
          </Link>
          — dan kijken we met u mee.
        </p>

        <div className="space-y-4">
          {[apiStatus, dbStatus].map((item) => (
            <div
              key={item.name}
              className="flex flex-col gap-3 rounded-lg border border-border bg-surface-card p-4 sm:flex-row sm:items-start sm:justify-between"
            >
              <div className="flex items-start gap-3">
                <StatusIcon item={item} />
                <div>
                  <p className="font-medium text-text">{item.customerLabel}</p>
                  <p className="text-sm text-text-muted">{statusLabel(item)}</p>
                  <p className="mt-2 text-sm text-text-muted">{statusHint(item)}</p>
                  {item.message && (
                    <p className="mt-2 text-sm text-amber-700 dark:text-amber-400">{item.message}</p>
                  )}
                </div>
              </div>
              {item.lastCheck && (
                <p className="shrink-0 text-xs text-text-muted sm:text-right">
                  Laatste meting: {new Date(item.lastCheck).toLocaleTimeString('nl-NL', { timeStyle: 'short' })}
                </p>
              )}
            </div>
          ))}
        </div>

        <div className="mt-8 rounded-xl border border-border/80 bg-surface px-4 py-4">
          <div className="flex items-start gap-3">
            <Headphones className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
            <div className="text-sm text-text-muted">
              <p className="font-medium text-text">Support</p>
              <p className="mt-1">
                Geen storing gemeld maar toch problemen? Controleer uw internetverbinding en of cookies voor deze site
                zijn toegestaan. Voor specifieke stappen in de app:{' '}
                <Link to="/account" className="font-medium text-primary hover:underline">
                  Account
                </Link>{' '}
                → <strong className="text-text">Hulp &amp; contact</strong> (zelfde links als in de footer onder
                &quot;Handleidingen&quot;).
              </p>
            </div>
          </div>
        </div>

        <details className="mt-6 rounded-lg border border-dashed border-border/80 bg-surface/50 px-4 py-3 text-sm text-text-muted">
          <summary className="cursor-pointer font-medium text-text">Technische details</summary>
          <p className="mt-3 text-xs leading-relaxed">
            Meting gebruikt <code className="rounded bg-surface-card px-1">GET /api/health</code> voor de online dienst
            en <code className="rounded bg-surface-card px-1">GET /api/health?deep=1</code> voor de verbinding met de
            database (Worker + D1). Dit is bedoeld voor support en beheerders; voor dagelijks gebruik volstaat de
            status hierboven.
          </p>
        </details>
      </AnimatedSection>
    </div>
  );
}
